import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { money, fmtDate, fmtDateTime, missionLabel, DOC_STATUS } from "@/lib/helpers";
import { SUB_STATE, visitesTxt } from "@/components/SubscriptionBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Plus, Search, History, SlidersHorizontal, Ban } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const defaultLabel = (visits) => `Abonnement ${visitesTxt(visits || 0)}`;
// Annulation possible seulement si rien n'a été décompté, aucune mission prévue et facture encore brouillon.
const cancellable = (s) => s.status !== "cancelled" && !s.used && s.remaining === s.visits && !s.planned && (!s.invoice_status || s.invoice_status === "draft");

export default function Subscriptions() {
  const { settings } = useSettings();
  const cur = settings?.currency || "EUR";
  const [subs, setSubs] = useState([]);
  const [clients, setClients] = useState([]);
  const [query, setQuery] = useState("");
  const [showCancelled, setShowCancelled] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [adjust, setAdjust] = useState(null);   // abonnement à corriger
  const [history, setHistory] = useState(null); // détail chargé (missions + corrections)
  const [toCancel, setToCancel] = useState(null);

  const load = useCallback(() => {
    api.get("/subscriptions").then((r) => setSubs(r.data));
    api.get("/clients").then((r) => setClients(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const q = query.trim().toLowerCase();
  // Le serveur renvoie le plus ancien en premier ; à l'écran, les plus récents d'abord.
  const shown = [...subs].reverse().filter((s) => (showCancelled || s.status !== "cancelled")
    && (!q || (s.client_name || "").toLowerCase().includes(q) || (s.label || "").toLowerCase().includes(q)));

  const openHistory = async (s) => {
    try { const { data } = await api.get(`/subscriptions/${s.id}`); setHistory(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const confirmCancel = async () => {
    const s = toCancel; setToCancel(null);
    try { await api.post(`/subscriptions/${s.id}/cancel`); toast.success("Abonnement annulé"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-overline text-accent">Prépayé</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">Abonnements</h1>
        </div>
        <Button className="gap-2 rounded-full shrink-0" onClick={() => setNewOpen(true)} data-testid="new-subscription-button"><Plus className="h-4 w-4" /> Abonnement</Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Rechercher un titulaire…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="subscription-search" />
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
          <input type="checkbox" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} data-testid="subscription-show-cancelled" /> Afficher les annulés
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {shown.length === 0 && <p className="text-sm text-muted-foreground">{q ? "Aucun abonnement ne correspond." : "Aucun abonnement."}</p>}
        {shown.map((s) => {
          const st = SUB_STATE[s.state] || SUB_STATE.active;
          const unpaid = s.status !== "cancelled" && s.invoice_status && s.invoice_status !== "paid";
          return (
            <div key={s.id} className={cn("bg-card border border-border rounded-2xl shadow-card p-5 space-y-3 min-w-0", s.status === "cancelled" && "opacity-60")} data-testid={`subscription-card-${s.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display font-bold text-lg truncate">{s.client_name || "—"}</div>
                  <div className="text-sm text-muted-foreground">{s.label}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className={cn("font-display font-black text-2xl leading-none", s.remaining <= 0 && s.status !== "cancelled" ? "text-red-600" : s.remaining <= 2 && s.status !== "cancelled" ? "text-amber-600" : "")} data-testid={`subscription-remaining-${s.id}`}>{s.remaining} / {s.visits}</div>
                  <div className="text-[10px] uppercase tracking-overline text-muted-foreground mt-1">restantes</div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className={cn("px-2.5 py-1 text-xs font-medium rounded-full", st.cls)} data-testid={`subscription-state-${s.id}`}>{st.label}{s.state === "overdrawn" ? ` (${s.remaining})` : ""}</span>
                {unpaid && <span className="px-2.5 py-1 text-xs font-medium rounded-full text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400" data-testid={`subscription-unpaid-${s.id}`}>Facture non payée</span>}
                {s.planned > 0 && <span className="text-xs text-muted-foreground">{s.planned} mission{s.planned > 1 ? "s" : ""} prévue{s.planned > 1 ? "s" : ""}</span>}
              </div>
              <div className="text-sm text-muted-foreground break-words">
                {s.visits} × {money(s.unit_price, cur)} = <span className="font-medium text-foreground">{money(s.price_ttc, cur)}</span>
                {" · "}vendu le {fmtDate(s.created_at)}
                {s.invoice_number && <> · facture <span className="font-mono">{s.invoice_number}</span>{s.invoice_status ? ` (${DOC_STATUS[s.invoice_status]?.label.toLowerCase()})` : ""}</>}
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" variant="outline" className="gap-1" onClick={() => openHistory(s)} data-testid={`subscription-history-${s.id}`}><History className="h-3.5 w-3.5" /> Historique</Button>
                {s.status !== "cancelled" && <Button size="sm" variant="outline" className="gap-1" onClick={() => setAdjust(s)} data-testid={`subscription-adjust-${s.id}`}><SlidersHorizontal className="h-3.5 w-3.5" /> Corriger le solde</Button>}
                {cancellable(s) && <Button size="sm" variant="ghost" className="gap-1 text-muted-foreground" onClick={() => setToCancel(s)} data-testid={`subscription-cancel-${s.id}`}><Ban className="h-3.5 w-3.5" /> Annuler</Button>}
              </div>
            </div>
          );
        })}
      </div>

      <NewSubscriptionDialog open={newOpen} setOpen={setNewOpen} clients={clients} cur={cur} onSaved={load} />
      <AdjustDialog sub={adjust} setSub={setAdjust} onSaved={load} />
      <HistoryDialog sub={history} setSub={setHistory} />

      <AlertDialog open={!!toCancel} onOpenChange={(o) => { if (!o) setToCancel(null); }}>
        <AlertDialogContent data-testid="subscription-cancel-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Annuler cet abonnement ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est définitive. La facture brouillon {toCancel?.invoice_number} sera supprimée : son numéro ne sera pas réutilisé, il manquera dans la suite de vos factures.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="subscription-cancel-confirm-no">Retour</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancel} data-testid="subscription-cancel-confirm-yes">Annuler l'abonnement</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function NewSubscriptionDialog({ open, setOpen, clients, cur, onSaved }) {
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({ client_id: "", visits: "", unit_price: "", label: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm({ client_id: "", visits: "", unit_price: "", label: "" });
    api.get("/settings/full").then((r) => setPlans(r.data.subscription_plans || [])).catch(() => setPlans([]));
  }, [open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const visits = parseInt(form.visits, 10) || 0;
  const unit = parseFloat(form.unit_price) || 0;
  const choosePlan = (p) => setForm((f) => ({ ...f, visits: String(p.visits), unit_price: String(p.unit_price), label: p.label || "" }));
  const sorted = [...clients].sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr"));

  const save = async () => {
    if (!form.client_id) { toast.error("Choisissez le titulaire de l'abonnement"); return; }
    if (visits < 1) { toast.error("Le nombre de visites doit être d'au moins 1"); return; }
    if (form.unit_price === "" || unit < 0) { toast.error("Indiquez le prix d'une visite"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/subscriptions", { client_id: form.client_id, visits, unit_price: unit, label: form.label.trim() });
      toast.success(`Abonnement créé — facture ${data.invoice_number} en brouillon dans Facturation`);
      setOpen(false); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">Nouvel abonnement</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Titulaire</Label>
            <Select value={form.client_id} onValueChange={(v) => set("client_id", v)}>
              <SelectTrigger data-testid="subscription-client"><SelectValue placeholder="Choisir un client" /></SelectTrigger>
              <SelectContent>{sorted.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Valable pour toutes ses voitures, et prêtable à une autre personne.</p>
          </div>
          <div className="space-y-1.5"><Label>Formule</Label>
            {plans.length === 0
              ? <p className="text-xs text-muted-foreground" data-testid="subscription-no-plan">Aucune formule enregistrée (Réglages → Formules d'abonnement) : saisissez le nombre de visites et le prix.</p>
              : <div className="flex flex-wrap gap-2">{plans.map((p, i) => (
                  <Button key={i} type="button" size="sm" variant="outline" onClick={() => choosePlan(p)} data-testid={`subscription-plan-${i}`}>
                    {p.label || defaultLabel(p.visits)} · {p.visits} × {money(p.unit_price, cur)}
                  </Button>))}</div>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Nombre de visites</Label><Input type="number" min="1" step="1" value={form.visits} onChange={(e) => set("visits", e.target.value)} data-testid="subscription-visits" /></div>
            <div className="space-y-1.5"><Label>Prix d'une visite</Label><Input type="number" min="0" step="0.01" value={form.unit_price} onChange={(e) => set("unit_price", e.target.value)} data-testid="subscription-unit-price" /></div>
          </div>
          <div className="space-y-1.5"><Label>Libellé sur la facture</Label>
            <Input value={form.label} onChange={(e) => set("label", e.target.value)} placeholder={defaultLabel(visits || 10)} data-testid="subscription-label" />
          </div>
          <div className="flex items-center justify-between bg-muted/50 border border-border rounded-xl px-4 py-3">
            <span className="text-sm text-muted-foreground">Total facturé</span>
            <span className="font-display font-black text-xl" data-testid="subscription-total">{money(Math.round(visits * unit * 100) / 100, cur)}</span>
          </div>
          <p className="text-xs text-muted-foreground">Une facture brouillon est créée dans Facturation : envoyez-la puis marquez-la payée comme une autre facture.</p>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={save} disabled={saving} data-testid="subscription-save">Créer l'abonnement</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdjustDialog({ sub, setSub, onSaved }) {
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  useEffect(() => { setDelta(""); setReason(""); }, [sub]);
  const n = parseInt(delta, 10) || 0;
  const save = async () => {
    if (!n) { toast.error("Indiquez une correction, par exemple -1 ou +2"); return; }
    if (!reason.trim()) { toast.error("Le motif est obligatoire"); return; }
    try {
      await api.post(`/subscriptions/${sub.id}/adjust`, { delta: n, reason: reason.trim() });
      toast.success("Solde corrigé"); setSub(null); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!sub} onOpenChange={(o) => { if (!o) setSub(null); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">Corriger le solde</DialogTitle></DialogHeader>
        {sub && <div className="space-y-3">
          <p className="text-sm text-muted-foreground">{sub.client_name} · {sub.label} · solde actuel <span className="font-medium text-foreground">{sub.remaining} / {sub.visits}</span></p>
          <div className="space-y-1.5"><Label>Correction (visites)</Label>
            <Input type="number" step="1" value={delta} onChange={(e) => setDelta(e.target.value)} placeholder="-1 pour retirer une visite, 2 pour en ajouter" data-testid="subscription-adjust-delta" />
            {n !== 0 && <p className="text-xs text-muted-foreground" data-testid="subscription-adjust-preview">Nouveau solde : {sub.remaining + n} / {sub.visits}</p>}
          </div>
          <div className="space-y-1.5"><Label>Motif (obligatoire)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex. reprise du tableau, visite offerte…" data-testid="subscription-adjust-reason" />
          </div>
        </div>}
        <DialogFooter><Button variant="outline" onClick={() => setSub(null)}>Annuler</Button><Button onClick={save} data-testid="subscription-adjust-save">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ sub, setSub }) {
  // Visites décomptées (missions terminées), missions prévues et corrections, les plus récentes d'abord.
  const rows = !sub ? [] : [
    ...(sub.missions || []).map((m) => ({ key: m.id, date: m.start, kind: m.status === "completed" ? "done" : "planned",
      text: `${missionLabel(m)} — ${m.client_name || "—"}${m.client_id !== sub.client_id ? " (prêt)" : ""}` })),
    ...(sub.adjustments || []).map((a) => ({ key: a.id, date: a.at, kind: "adjust",
      text: `Correction ${a.delta > 0 ? "+" : ""}${a.delta} — ${a.reason}${a.by_name ? ` (${a.by_name})` : ""}` })),
  ].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  const tag = { done: "Visite décomptée", planned: "Prévue", adjust: "Correction" };
  return (
    <Dialog open={!!sub} onOpenChange={(o) => { if (!o) setSub(null); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Historique</DialogTitle></DialogHeader>
        {sub && <div className="space-y-3">
          <p className="text-sm text-muted-foreground">{sub.client_name} · {sub.label} · {sub.remaining} / {sub.visits} restantes · {sub.used} visite{sub.used > 1 ? "s" : ""} décomptée{sub.used > 1 ? "s" : ""}</p>
          <div className="border border-border rounded-xl divide-y divide-border max-h-80 overflow-y-auto" data-testid="subscription-history-list">
            {rows.length === 0 && <p className="p-4 text-sm text-muted-foreground">Aucune visite ni correction pour l'instant.</p>}
            {rows.map((r) => (
              <div key={r.key} className="px-4 py-2.5 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className={cn("text-[10px] uppercase tracking-overline", r.kind === "adjust" ? "text-amber-600" : r.kind === "done" ? "text-emerald-600" : "text-muted-foreground")}>{tag[r.kind]}</span>
                  <span className="text-xs text-muted-foreground">{fmtDateTime(r.date)}</span>
                </div>
                <div>{r.text}</div>
              </div>
            ))}
          </div>
        </div>}
      </DialogContent>
    </Dialog>
  );
}
